import { gsap } from 'gsap';

// Enum for animation directions
const Directions = {
    TopToBottom: {
        firstTransition: '+',
        secondTransition: '-',
    },
    BottomToTop: {
        firstTransition: '-',
        secondTransition: '+',
    },
};

class TimelineSlider {
    constructor() {
        this.direction = Directions.TopToBottom;
        this.$viewport = $('#timeline-circular-viewport');
        this.$timeline = $('#timeline-circular');
        this.availableYears = this.$timeline.find('.year-btn');
        this.currentIndex = 0;
        this.currentYear = this.availableYears[this.currentIndex].dataset.year;
        this.isAnimating = false;
        this.dragState = {
            isDragging: false,
            startX: 0,
            startScrollLeft: 0,
            moved: false,
        };

        this.updateAdjacentYears();
        this.centerActiveYear(false);
        this.updateImagesAndTexts();

        this.eventListeners();
    }

    // Update the previous and next years
    updateAdjacentYears() {
        this.prevYear =
            this.availableYears[
                this.currentIndex > 0
                    ? this.currentIndex - 1
                    : this.availableYears.length - 1
                ].dataset.year;

        this.nextYear =
            this.availableYears[
                this.currentIndex < this.availableYears.length - 1
                    ? this.currentIndex + 1
                    : 0
                ].dataset.year;
    }

    centerActiveYear(animate = true) {
        const activeYear = this.availableYears[this.currentIndex];

        if (!activeYear || !this.$viewport.length) {
            return;
        }

        const viewportWidth = this.$viewport.innerWidth();
        const yearOffset = activeYear.offsetLeft + activeYear.offsetWidth / 2;
        const targetX = Math.max(0, yearOffset - viewportWidth / 2);
        const maxScroll = Math.max(0, this.$timeline.outerWidth() - viewportWidth);
        const finalX = Math.min(targetX, maxScroll);

        this.$viewport.stop(true);

        if (animate) {
            this.$viewport.animate({ scrollLeft: finalX }, 350);
        } else {
            this.$viewport.scrollLeft(finalX);
        }
    }

    syncYearFromViewport(animate = true) {
        if (!this.$viewport.length) {
            return;
        }

        const viewportWidth = this.$viewport.innerWidth();
        const viewportCenter = this.$viewport.scrollLeft() + viewportWidth / 2;
        let nearestIndex = this.currentIndex;
        let nearestDistance = Number.POSITIVE_INFINITY;

        this.availableYears.each((index, element) => {
            const elementCenter = element.offsetLeft + element.offsetWidth / 2;
            const distance = Math.abs(elementCenter - viewportCenter);

            if (distance < nearestDistance) {
                nearestDistance = distance;
                nearestIndex = index;
            }
        });

        if (nearestIndex !== this.currentIndex) {
            this.handleYearButtonClick(nearestIndex, animate);
        } else {
            this.centerActiveYear(animate);
        }
    }

    // Update the displayed images and texts
    updateImagesAndTexts() {
        const currentIndex = this.currentIndex;
        const availableTitles = $('#all-content .title');
        const availableTexts = $('#all-content .description');
        const availableImages = $('#all-content img');

        const currentTitle = availableTitles[this.currentIndex].textContent;
        const currentText = availableTexts[this.currentIndex].textContent;
        const currentImageSrc = availableImages[this.currentIndex].src;

        $('#infos-circular .current-title').html(currentTitle);
        $('#infos-circular .current-txt').html(currentText);
        $('#current-img-container-circular .current-img').attr(
            'src',
            currentImageSrc
        );
        $('#current-img-container-circular .current-img-link').attr(
            'href',
            currentImageSrc
        );

        $('#history-modal #modal-year').html(this.currentYear);
        $('#history-modal #modal-title').html(currentTitle);
        $('#history-modal #modal-txt .text-content').html(currentText);

        const rawText = currentText.replace(/(<([^>]+)>)/gi, '');
        const showModalBtn = rawText.length >= 300;
        $('#open-modal-btn').css({
            display: showModalBtn ? 'flex' : 'none',
        });
        $('#infos-circular .current-txt').toggleClass('line-clamp-3', showModalBtn);
    }

    // Animate the content
    animate() {
        // Verrouille l'interaction le temps de la transition (évite les tweens qui se chevauchent = saccades)
        this.setInteractionLock(true);

        gsap.to('#infos-circular .current-content', {
            duration: 0.4,
            stagger: 0.1,
            ease: 'power1.inOut',
            opacity: 0,
            y: -10,
        });

        gsap.to('#infos-circular .current-year', {
            duration: 0.6,
            stagger: 0.1,
            ease: 'power1.inOut',
            innerText: this.currentYear,
            snap: {
                innerText: 1,
            },
        });

        gsap.to('#prev-year', {
            duration: 0.6,
            stagger: 0.1,
            ease: 'power1.inOut',
            innerText: this.prevYear,
            snap: {
                innerText: 1,
            },
        });

        gsap.to('#next-year', {
            duration: 0.6,
            stagger: 0.1,
            ease: 'power1.inOut',
            innerText: this.nextYear,
            snap: {
                innerText: 1,
            },
        });

        gsap.to('#current-img-container-circular', {
            duration: 0.4,
            stagger: 0.4,
            ease: 'power1.inOut',
            y: `${this.direction.firstTransition}=120%`,
            onComplete: () => {
                this.updateImagesAndTexts();
                gsap.to('#infos-circular .current-content', {
                    duration: 0.4,
                    stagger: 0.1,
                    ease: 'power1.inOut',
                    opacity: 1,
                    y: 0,
                });

                gsap.set('#current-img-container-circular', {
                    y: `${this.direction.secondTransition}120%`,
                });

                gsap.to('#current-img-container-circular', {
                    duration: 0.4,
                    stagger: 0.1,
                    ease: 'power1.inOut',
                    y: 0,
                    onComplete: () => {
                        this.setInteractionLock(false);
                    },
                });
            },
        });
    }

    // Verrouille/déverrouille les flèches (attribut disabled natif) pendant la transition
    setInteractionLock(locked) {
        this.isAnimating = locked;
        $('.history-arrow-circular').prop('disabled', locked);
    }

    // Set up event listeners
    eventListeners() {
        this.$timeline.on('pointerup click', '.year-btn', (event) => {
            if (event.type === 'pointerup' && this.dragState.moved) {
                return;
            }

            const index = this.availableYears.index(event.currentTarget);

            if (index !== -1) {
                console.log('[history-circular] year-btn interaction', {
                    eventType: event.type,
                    index,
                    year: event.currentTarget.dataset.year,
                    currentIndex: this.currentIndex,
                    currentYear: this.currentYear,
                });
                event.preventDefault();
                this.handleYearButtonClick(index);
            }
        });

        $('.history-arrow-circular')
            .add('.adj-year')
            .each((index, element) => {
                $(element).on('click', () => {
                    this.handleArrowClick(element);
                });
            });

        this.bindTimelineDrag();
    }

    bindTimelineDrag() {
        if (!this.$viewport.length) {
            return;
        }

        const viewport = this.$viewport.get(0);

        this.$viewport.on('pointerdown', (event) => {
            if ($(event.target).closest('.year-btn, .history-arrow-circular, .adj-year').length) {
                return;
            }

            if (event.button !== 0 && event.pointerType === 'mouse') {
                return;
            }

            this.dragState.isDragging = true;
            this.dragState.moved = false;
            this.dragState.startX = event.clientX;
            this.dragState.startScrollLeft = viewport.scrollLeft;

            console.log('[history-circular] drag start', {
                startX: this.dragState.startX,
                startScrollLeft: this.dragState.startScrollLeft,
            });

            this.$viewport.stop(true);
            viewport.setPointerCapture?.(event.pointerId);
            this.$viewport.addClass('is-dragging');
        });

        this.$viewport.on('pointermove', (event) => {
            if (!this.dragState.isDragging) {
                return;
            }

            const deltaX = event.clientX - this.dragState.startX;

            if (Math.abs(deltaX) > 4) {
                this.dragState.moved = true;
            }

            viewport.scrollLeft = this.dragState.startScrollLeft - deltaX;

            if (this.dragState.moved) {
                console.log('[history-circular] dragging', {
                    deltaX,
                    scrollLeft: viewport.scrollLeft,
                });
            }
        });

        const endDrag = () => {
            if (!this.dragState.isDragging) {
                return;
            }

            this.dragState.isDragging = false;
            this.$viewport.removeClass('is-dragging');

            if (this.dragState.moved) {
                console.log('[history-circular] drag end -> sync from viewport', {
                    scrollLeft: this.$viewport.scrollLeft(),
                });
                this.syncYearFromViewport(true);
            }

            this.dragState.moved = false;
        };

        this.$viewport.on('pointerup pointercancel pointerleave', endDrag);
    }

    // Handle click on year button
    handleYearButtonClick(index, animate = true) {
        if (this.isAnimating) {
            return;
        }

        this.availableYears.removeClass('current');
        $(this.availableYears[index]).addClass('current');

        const targetYear = $(this.availableYears[index]).attr('data-year');

        console.log('[history-circular] handleYearButtonClick', {
            index,
            targetYear,
            currentIndex: this.currentIndex,
            currentYear: this.currentYear,
            animate,
        });

        if (index === this.currentIndex) {
            this.centerActiveYear(animate);
            return;
        }

        this.direction =
            this.currentYear < targetYear
                ? Directions.TopToBottom
                : Directions.BottomToTop;

        console.log('[history-circular] direction selected', {
            direction: this.direction === Directions.TopToBottom ? 'TopToBottom' : 'BottomToTop',
        });

        this.currentYear = targetYear;
        this.currentIndex = index;

        this.updateAdjacentYears();
        this.centerActiveYear(animate);
        this.animate();
    }

    // Handle click on arrow button
    handleArrowClick(element) {
        if (this.isAnimating) {
            return;
        }

        console.log('[history-circular] arrow click', {
            id: element.id,
            currentIndex: this.currentIndex,
            currentYear: this.currentYear,
        });

        if (element.id === 'circular-prev' || element.id === 'prev-year') {
            this.direction = Directions.BottomToTop;
            this.currentIndex =
                this.currentIndex > 0
                    ? this.currentIndex - 1
                    : this.availableYears.length - 1;
        } else if (element.id === 'circular-next' || element.id === 'next-year') {
            this.direction = Directions.TopToBottom;
            this.currentIndex =
                this.currentIndex === this.availableYears.length - 1
                    ? 0
                    : this.currentIndex + 1;
        }

        this.currentYear = this.availableYears[this.currentIndex].dataset.year;
        this.updateAdjacentYears();
        this.centerActiveYear();

        console.log('[history-circular] arrow resolved', {
            currentIndex: this.currentIndex,
            currentYear: this.currentYear,
            prevYear: this.prevYear,
            nextYear: this.nextYear,
        });

        this.availableYears.removeClass('current');
        $(this.availableYears[this.currentIndex]).addClass('current');

        if ($(element).hasClass('adj-year')) {
            $(element).addClass('active');
        }

        this.animate();
    }
}

function handleModal() {
    const openBtn = $('#open-modal-btn');
    const closeBtn = $('#close-modal-btn');
    const modal = $('#history-modal');
    const modalContent = $('#history-modal #modal-content');

    let isOpen = false;

    // Helper function for modal animations
    const toggleModal = (action) => {
        const animationDuration = 300;
        if (action === 'open') {
            $(modal).fadeIn(animationDuration, () => {
                $(modalContent).slideDown(animationDuration);
                isOpen = true;
                document.body.style.overflow = 'hidden';
            });
        } else if (action === 'close') {
            $(modalContent).slideUp(animationDuration, () => {
                $(modal).fadeOut(animationDuration);
                isOpen = false;
                document.body.style.overflow = 'auto';
            });
        }
    };

    // Event listener for opening the modal
    openBtn.on('click', () => toggleModal('open'));

    // Event listener for closing the modal
    closeBtn.on('click', () => toggleModal('close'));

    // Event listener for closing the modal on Escape key press
    $(document).on('keyup', function (e) {
        if (e.key === 'Escape' && isOpen) {
            toggleModal('close');
        }
    });
}

$(document).ready(function () {
    if (document.querySelector('.history')) {
        new TimelineSlider();
        handleModal();
    }
});